/**
 * Deposit Methods API Service
 * 
 * Manages deposit methods data provided by PHP.
 * Follows Single Responsibility Principle - only manages deposit methods data.
 * 
 * @package betbeast
 */

/**
 * Parse a limit bound. A blank/absent bound means "no bound" and must stay null:
 * coercing it to 0 would write max="0" on the amount input and make every amount
 * invalid.
 *
 * @param {string|number|null} value - Raw bound from PHP
 * @returns {number|null} Numeric bound, or null when unset
 */
function toBound(value) {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
}

export class DepositMethodsAPI {
    constructor() {
        this.methods = [];
        this.isLoaded = false;
        this.methodsMap = new Map();
        this.variantsMap = new Map();
    }

    /**
     * Load deposit methods from PHP-provided data
     * @param {Array} methods - Array of deposit methods from PHP
     * @returns {Array} Array of deposit methods
     */
    loadDepositMethods(methods) {
        if (!Array.isArray(methods)) {
            console.warn('⚠️ Invalid deposit methods data provided');
            return [];
        }
        
        this.methods = methods;
        this.isLoaded = true;
        this.buildMethodsMap();

        return this.methods;
    }
    
    /**
     * Build the lookup maps.
     *
     * Two maps because two different questions are asked of this data: the API
     * code answers "is this method supported / what is it called", while the
     * variant key identifies exactly one card. Several cards can share an API
     * code (e.g. Interac ACH and Interac ETO) with different limits, so anything
     * per-card must be looked up by variant — keying only by code would let the
     * last variant overwrite its siblings.
     */
    buildMethodsMap() {
        this.methodsMap.clear();
        this.variantsMap.clear();

        this.methods.forEach(method => {
            if (method.variant_key) {
                this.variantsMap.set(method.variant_key, method);
            }

            // First variant wins, so code lookups stay stable as cards are added.
            if (!this.methodsMap.has(method.code)) {
                this.methodsMap.set(method.code, method);
            }
            if (method.payment_method
                && method.payment_method !== method.code
                && !this.methodsMap.has(method.payment_method)) {
                this.methodsMap.set(method.payment_method, method);
            }
        });
    }
    
    /**
     * Get all deposit methods
     * @returns {Array} Array of deposit methods
     */
    getMethods() {
        return this.methods;
    }
    
    /**
     * Get a specific deposit method by code
     * @param {string} code - Method code
     * @returns {Object|null} Deposit method or null
     */
    getMethod(code) {
        return this.methodsMap.get(code) || null;
    }
    
    /**
     * Check if a method is supported
     * @param {string} code - Method code
     * @returns {boolean} True if method is supported
     */
    isMethodSupported(code) {
        return this.methodsMap.has(code);
    }
    
    /**
     * Get method display name
     * @param {string} code - Method code
     * @returns {string} Display name
     */
    getMethodName(code) {
        const method = this.getMethod(code);
        return method ? method.provider_name : code;
    }
    
    /**
     * Get the deposit limits for one card.
     *
     * Keyed by variant, because cards sharing an API code carry their own
     * WP-configured range. A bound that is not configured comes back as null,
     * meaning "no bound" — never 0.
     *
     * @param {string} variantKey - Card identifier (data-variant-key)
     * @param {string|null} fallbackCode - API code, used for legacy cards with no variant
     * @returns {Object} Object with min, max and feeRate
     */
    getLimitsForVariant(variantKey, fallbackCode = null) {
        const method = this.variantsMap.get(variantKey) || this.getMethod(fallbackCode);
        if (!method) {
            return { min: null, max: null, feeRate: 0 };
        }

        return {
            min: toBound(method.minimum_deposit),
            max: toBound(method.maximum_deposit),
            feeRate: parseFloat(method.fee_rate || 0)
        };
    }
    
    /**
     * Check if methods are loaded
     * @returns {boolean} True if methods are loaded
     */
    isReady() {
        return this.isLoaded;
    }
    
    /**
     * Get supported method codes
     * @returns {Array<string>} Array of supported method codes
     */
    getSupportedCodes() {
        return this.methods.map(method => method.code);
    }
    
    /**
     * Map frontend method identifier to API method code
     * Since we now use API codes directly in the template, this just validates the code
     * @param {string} methodCode - Method code from data-payment-method attribute
     * @returns {string|null} API method code or null if not supported
     */
    mapUIMethodToAPICode(methodCode) {
        // The template now passes API codes directly, so just validate
        if (this.isMethodSupported(methodCode)) {
            return methodCode;
        }
        
        // Fallback: try to find by partial match
        const lowerMethod = methodCode.toLowerCase();
        for (const method of this.methods) {
            const code = method.code || '';
            const providerName = method.provider_name || '';
            const paymentMethod = method.payment_method || '';
            
            if (code.toLowerCase().includes(lowerMethod) ||
                providerName.toLowerCase().includes(lowerMethod) ||
                paymentMethod.toLowerCase().includes(lowerMethod)) {
                return method.code;
            }
        }
        
        return null;
    }
}
